import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useBrands } from "../../contexts/BrandContext";
import {
  CheckCircle2, Clock, Building2, MapPin, RefreshCw, Search, Inbox, Pencil, Trash2,
} from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { formatDateFr } from "../../lib/dateFormat";
import { effectiveShiftHours, unpaidBreakHours } from "../../lib/shiftHours";

function diffHours(s, e) {
  if (!s || !e) return null;
  try {
    const [sh, sm] = s.split(":").map(Number);
    const [eh, em] = e.split(":").map(Number);
    const minutes = (eh * 60 + em) - (sh * 60 + sm);
    return (minutes / 60).toFixed(2);
  } catch { return null; }
}

/**
 * Heures effectives d'un pointage terminé : durée pointée (clock_out − clock_in)
 * moins les pauses non rémunérées héritées du shift planifié associé.
 * Si l'entrée n'a pas de pauses (pointage libre sans shift), renvoie la durée brute.
 */
function effectiveHours(entry) {
  const raw = effectiveShiftHours({
    start_time: entry.clock_in,
    end_time: entry.clock_out,
    breaks: entry.breaks || [],
  });
  return Math.round(raw * 100) / 100;
}

const ZONE_BADGE = (geo, siteName, distance_m) => {
  switch (geo) {
    case "in_zone":
      return { label: siteName ? siteName : "Zone OK", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" };
    case "out_of_zone":
      return {
        label: `Hors zone${distance_m ? ` · ${distance_m} m` : ""}`,
        cls: "bg-red-50 text-red-700 border-red-200",
      };
    case "remote":
      return { label: "Télétravail", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" };
    case "on_platform":
      return { label: "Plateforme en ligne", cls: "bg-sky-50 text-sky-700 border-sky-200" };
    case "on_field":
      return { label: "Terrain", cls: "bg-amber-50 text-amber-700 border-amber-200" };
    case "no_site":
      return { label: "Aucun site géo-localisé", cls: "bg-amber-50 text-amber-700 border-amber-200" };
    default:
      return null;
  }
};

/**
 * Onglet "Terminés" — liste des pointages clôturés (entrée + sortie).
 *
 * Présentation calquée sur EquipmentRequestsTab.jsx : barre colorée latérale,
 * badges majuscules, liste paginée par divider, recherche dédiée. Filtrage
 * complémentaire par plage de dates.
 */
export default function PointageTermineTab({
  brands = [],
  users = [],
  sites = [],
  isAdmin = false,
  onEdit,
  onDelete,
  refreshSignal = 0,
}) {
  const { selectedBrandId } = useBrands();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setRefreshing(true);
      const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
      const { data } = await api.get("/timeentries", { params });
      setList(data || []);
    } catch {
      setList([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedBrandId]);

  useEffect(() => { load(); }, [load, refreshSignal]);

  const closedEntries = useMemo(
    () => list.filter((t) => !!t.clock_out),
    [list],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return closedEntries.filter((t) => {
      if (dateFrom && (!t.date || t.date < dateFrom)) return false;
      if (dateTo && (!t.date || t.date > dateTo)) return false;
      if (!q) return true;
      const u = users.find((x) => x.id === t.collaborateur_id);
      const b = brands.find((x) => x.id === t.brand_id);
      const s = sites.find((x) => x.id === (t.site_id || t.nearest_site_id));
      return (
        (u?.name || "").toLowerCase().includes(q)
        || (u?.email || "").toLowerCase().includes(q)
        || (b?.name || "").toLowerCase().includes(q)
        || (s?.name || "").toLowerCase().includes(q)
        || (t.address || "").toLowerCase().includes(q)
      );
    });
  }, [closedEntries, search, dateFrom, dateTo, users, brands, sites]);

  return (
    <div className="space-y-4" data-testid="pointage-termine-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="label-uppercase mb-1 inline-flex items-center gap-1.5">
            <CheckCircle2 size={11} /> Historique clôturé
          </p>
          <h2 className="font-display font-semibold text-xl text-zinc-900">
            Pointages terminés
          </h2>
          <p className="text-xs text-zinc-600 mt-1 font-mono">
            Entrées et sorties enregistrées · {closedEntries.length} pointage{closedEntries.length > 1 ? "s" : ""} clôturé{closedEntries.length > 1 ? "s" : ""}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={load}
          disabled={refreshing}
          data-testid="pointage-termine-refresh"
        >
          <RefreshCw size={13} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          Rafraîchir
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm p-3 space-y-3">
        <div>
          <Label className="label-uppercase mb-1.5 block inline-flex items-center gap-1.5">
            <Search size={10} /> Recherche
          </Label>
          <Input
            placeholder="Collaborateur, département, site ou adresse…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="pointage-termine-search"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="label-uppercase mb-1.5 block">Date du</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              data-testid="pointage-termine-date-from"
            />
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Date au</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              data-testid="pointage-termine-date-to"
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        {loading && filtered.length === 0 ? (
          <p className="text-sm text-zinc-500 font-mono py-10 text-center">Chargement…</p>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center" data-testid="pointage-termine-empty">
            <Inbox className="mx-auto text-zinc-300 mb-3" size={36} />
            <p className="text-sm text-zinc-500 font-mono">
              {closedEntries.length === 0
                ? "Aucun pointage terminé pour le moment."
                : "Aucun pointage ne correspond à votre recherche."}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {filtered.map((t) => {
              const u = users.find((x) => x.id === t.collaborateur_id);
              const brand = brands.find((b) => b.id === t.brand_id);
              const site = sites.find((s) => s.id === (t.site_id || t.nearest_site_id));
              const hours = diffHours(t.clock_in, t.clock_out);
              const netHours = effectiveHours(t);
              const unpaid = Math.round(unpaidBreakHours(t.breaks) * 100) / 100;
              const zone = ZONE_BADGE(t.geo_status, site?.name, t.distance_m);
              return (
                <div
                  key={t.id}
                  className="p-4 flex items-start gap-3 flex-wrap"
                  data-testid={`pointage-termine-row-${t.id}`}
                >
                  <span
                    className="h-12 w-1 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: brand?.color || "#16A34A" }}
                  />
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-emerald-50 border-emerald-200 text-emerald-800">
                        <CheckCircle2 size={10} /> Terminé
                      </span>
                      {u?.id ? (
                        <Link
                          to={`/collaborateurs/${u.id}/dossier`}
                          className="text-sm font-medium text-zinc-900 hover:text-blue-700 hover:underline underline-offset-2"
                          data-testid={`pointage-termine-collab-${t.id}`}
                        >
                          {u.name}
                        </Link>
                      ) : (
                        <p className="text-sm font-medium text-zinc-900">—</p>
                      )}
                      {hours && (
                        <span
                          className="text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider bg-zinc-50 border-zinc-200 text-zinc-700"
                          data-testid={`pointage-termine-hours-${t.id}`}
                          title={unpaid > 0 ? `Brut ${hours} h − ${unpaid} h pause non rém. = ${netHours} h effectives` : `${hours} h`}
                        >
                          {netHours} h
                          {unpaid > 0 && (
                            <span className="ml-1 text-[9px] normal-case text-zinc-500">(brut {hours} h)</span>
                          )}
                        </span>
                      )}
                      {zone && (
                        <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 border rounded-sm tracking-wider ${zone.cls}`}>
                          {zone.label}
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 mt-1 text-[11px] font-mono text-zinc-600">
                      <p className="inline-flex items-center gap-1.5">
                        <Clock size={11} className="text-zinc-400" />
                        <b className="text-zinc-800">{t.clock_in}</b>
                        <span className="text-zinc-400">→</span>
                        <b className="text-zinc-800">{t.clock_out}</b>
                        {t.date && <span className="text-zinc-400">· {formatDateFr(t.date)}</span>}
                      </p>
                      <p className="inline-flex items-center gap-1.5">
                        <Building2 size={11} className="text-zinc-400" /> {brand?.name || "—"}
                      </p>
                      {site?.name && (
                        <p className="inline-flex items-center gap-1.5">
                          <MapPin size={11} className="text-zinc-400" /> {site.name}
                        </p>
                      )}
                    </div>
                    {t.address && (
                      <p className="text-[11px] text-zinc-500 font-mono mt-1 inline-flex items-center gap-1.5">
                        <MapPin size={10} className="text-zinc-400" />
                        <span className="truncate-2">{t.address}</span>
                      </p>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit?.(t)}
                        className="h-8 px-2 text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                        data-testid={`pointage-termine-edit-${t.id}`}
                        title="Modifier"
                      >
                        <Pencil size={13} />
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          setBusyId(t.id);
                          try { await onDelete?.(t); await load(); }
                          finally { setBusyId(null); }
                        }}
                        disabled={busyId === t.id}
                        className="h-8 px-2 text-red-700 border-red-200 hover:bg-red-50"
                        data-testid={`pointage-termine-delete-${t.id}`}
                        title="Supprimer"
                      >
                        <Trash2 size={13} />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
